import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { X, Check, RotateCcw } from 'lucide-react';

// Aspect je nach Slot-Modus:
//  - face:  3:4  (Schultergesicht, wirkt wie WhatsApp-Profilbild)
//  - body:  9:16 (klassisches Hochkant-Smartphone, ganzer Körper passt vom Kopf bis zu den Füßen)
const ASPECT_FACE = 3 / 4;
const ASPECT_BODY = 9 / 16;
const OUTPUT_FACE = { width: 1024, height: 1365 };  // 3:4
const OUTPUT_BODY = { width: 1024, height: 1820 };  // 9:16 (gerundet auf gerade Pixel)

/**
 * Schneidet ein Bild auf den vom User gewählten Rahmen zu und gibt eine
 * neue File-Instanz zurück (JPEG, sRGB, ~90 % Quality).
 */
async function cropToFile(imageSrc, cropPx, originalName, output) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = output.width;
  canvas.height = output.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    img,
    cropPx.x, cropPx.y, cropPx.width, cropPx.height,
    0, 0, canvas.width, canvas.height,
  );

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9),
  );
  const baseName = (originalName || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}_cropped.jpg`, { type: 'image/jpeg' });
}

/**
 * Modal mit react-easy-crop. Pinch-Zoom auf Touch, Maus-Wheel + Drag auf Desktop.
 *
 * Props:
 *   file          — die vom User ausgewählte File-Instanz
 *   slotLabel     — Anzeige im Hinweistext (z.B. "Kopf vorne", "Ganzkörper vorne")
 *   mode          — 'face' (Default) | 'body' — bestimmt nur den Hint-Text
 *   onConfirm(croppedFile)
 *   onCancel()
 */
const FaceCropperModal = ({ file, slotLabel, mode = 'face', onConfirm, onCancel }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPx, setCroppedAreaPx] = useState(null);
  const [working, setWorking] = useState(false);

  const aspect = mode === 'body' ? ASPECT_BODY : ASPECT_FACE;
  const output = mode === 'body' ? OUTPUT_BODY : OUTPUT_FACE;

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPx) => {
    setCroppedAreaPx(areaPx);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPx) return;
    setWorking(true);
    try {
      const cropped = await cropToFile(imageSrc, croppedAreaPx, file?.name, output);
      onConfirm(cropped);
    } catch (e) {
      console.error('Crop failed', e);
      alert('Zuschnitt fehlgeschlagen. Bitte erneut versuchen.');
      setWorking(false);
    }
  };

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  if (!file) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
        <button
          onClick={onCancel}
          className="text-white/70 hover:text-white p-2 -ml-2"
          disabled={working}
        >
          <X className="w-6 h-6" />
        </button>
        <div className="text-center">
          <h2 className="text-white font-bold text-base sm:text-lg leading-tight">
            {mode === 'body'
              ? 'Schau, dass der ganze Körper im Rahmen ist'
              : 'Schau, dass das Gesicht im Rahmen ist'}
          </h2>
          <p className="text-white/50 text-xs mt-0.5">
            {slotLabel} · ziehen · zoomen
          </p>
        </div>
        <button
          onClick={reset}
          className="text-white/70 hover:text-white p-2 -mr-2"
          disabled={working}
          title="Zurücksetzen"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Cropper-Bühne (nimmt verfügbaren Platz) */}
      <div className="relative flex-1 bg-black">
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape="rect"
            showGrid={true}
            objectFit="contain"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            minZoom={1}
            maxZoom={5}
            zoomSpeed={0.05}
            style={{
              containerStyle: { background: '#000' },
              cropAreaStyle: { border: '3px solid #10B981', color: 'rgba(0,0,0,0.6)' },
            }}
          />
        )}
      </div>

      {/* Footer mit Zoom-Slider + Buttons */}
      <div className="flex-shrink-0 bg-black border-t border-white/10 px-4 py-4 space-y-4">
        {/* Zoom-Slider (für Desktop / fallback) */}
        <div className="flex items-center gap-3">
          <span className="text-white/60 text-xs w-10">Zoom</span>
          <input
            type="range"
            min={1}
            max={5}
            step={0.02}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="flex-1 accent-[#10B981]"
            disabled={working}
          />
          <span className="text-white/60 text-xs w-12 text-right tabular-nums">
            {zoom.toFixed(2)}×
          </span>
        </div>

        {/* Aktions-Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={working}
            className="flex-1 px-5 py-3 rounded-xl font-semibold text-white/80 border-2 border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleConfirm}
            disabled={!croppedAreaPx || working}
            className={`flex-1 px-5 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
              croppedAreaPx && !working
                ? 'bg-gradient-to-br from-[#10B981] to-[#059669] text-black glow-green'
                : 'bg-white/10 text-white/40 cursor-not-allowed border-2 border-white/20'
            }`}
          >
            <Check className="w-5 h-5" />
            {working ? 'Wird übernommen ...' : 'Übernehmen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaceCropperModal;
